import type { FpStandardsPerformanceRoute } from 'teacher-ui-client/reporting/ui-scope/fast-phonics/standards-performance/route'
import type { FpStandardsPerformanceController } from 'teacher-ui-client/reporting/ui-scope/fast-phonics/standards-performance/controller'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import RouteTemplate from 'ember-route-template'
import { t } from 'ember-intl'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { ReportingPrintButton } from 'teacher-ui-client/components/reporting/print-button'
import StudentSearchComponent from 'teacher-ui-client/components/student-search'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import {
  StandardsOverview,
  type StandardsOverviewReportData,
} from 'teacher-ui-client/components/reporting/standards-performance/overview'
import {
  StandardsResults,
  type StandardsResultsReportData,
} from 'teacher-ui-client/components/reporting/standards-performance/results'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { trackedFunction } from 'reactiveweb/function'
import { exportFilename, toRequestScope } from 'teacher-ui-client/services/reporting'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import { task } from 'ember-concurrency'
import { NoReportValue } from 'teacher-ui-client/components/reporting/no-report-value'
import { and } from 'ember-truth-helpers'
import { array } from '@ember/helper'
import { PerformanceOverview } from 'teacher-ui-client/components/reporting/performance-overview'
import { StandardsSetUpdatedAlert } from 'teacher-ui-client/components/standards-set/updated-alert'
import type SessionService from 'teacher-ui-client/services/session'
import { NoStandardsSetAlert } from 'teacher-ui-client/components/standards-set/no-standards-set-alert'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { ContentLevelSelector } from 'teacher-ui-client/components/reporting/content-level-selector'

interface Signature {
  Args: {
    model: ModelFor<FpStandardsPerformanceRoute>
    controller: FpStandardsPerformanceController
  }
}

export class FpStandardsPerformancePage extends Component<Signature> {
  @service reporting!: ReportingService
  @service session!: SessionService

  fetchOverviewReport = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.FP,
      requestScope,
      reportName: 'standards-performance/overview',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      contentLevel: this.args.controller.contentLevel,
    })
    return this.reporting.fetchReport<StandardsOverviewReportData>(url, query)
  })

  @use
  latestOverviewReport = keepLatest({
    value: () => this.fetchOverviewReport.value,
    when: () => this.fetchOverviewReport.isPending,
  })

  fetchResultsReport = trackedFunction(this, async () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.FP,
      requestScope,
      reportName: 'standards-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      page: { number: this.args.controller.pageNumber, size: this.args.controller.perPage },
      contentLevel: this.args.controller.contentLevel,
      sort: this.args.controller.sort,
    })
    return this.reporting.fetchReport<StandardsResultsReportData>(url, query)
  })

  @use
  latestResultsReport = keepLatest({
    value: () => this.fetchResultsReport.value,
    when: () => this.fetchResultsReport.isPending,
  })

  exportResultsCsv = task({ drop: true }, async () => {
    const requestScope = toRequestScope(this.args.model.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.FP,
      requestScope,
      reportName: 'standards-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      contentLevel: this.args.controller.contentLevel,
      sort: this.args.controller.sort,
    })
    const filename = exportFilename(Product.FP, 'standards-performance', '.csv')

    await this.reporting.exportReportCsv(url, query, filename)
  })

  get latestResultsReportData() {
    return this.latestResultsReport?.results.length ? this.latestResultsReport : undefined
  }

  get latestOverviewReportData() {
    return this.latestOverviewReport?.overview.total_lessons_completed ? this.latestOverviewReport : undefined
  }

  switchContentLevel = (value: string) => {
    this.args.controller.pageNumber = 1
    this.args.controller.contentLevel = value
  }

  // ReadingEggs data only comes in three content levels Kindergarden, Grade 1, Grade 2
  get contentLevelPositions() {
    return [1, 2, 3]
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get isMissingStandards() {
    return !this.session.currentSchool.standardsSet
  }

  get isOneRosterEnabled() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.standardsPerformance"}} />

    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <ReportingPeriodSelect @disabled={{this.isMissingStandards}} />
      <ReportingPrintButton @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <StudentSearchComponent
        @studentRouteName="reporting.ui-scope.fast-phonics.student-summary"
        @students={{@model.scopedStudents}}
        @includeSelectAll={{false}}
      />
    </ReportingControls>
    <div class="space-y-6">
      <ContentLevelSelector
        @switchContentLevel={{this.switchContentLevel}}
        @selectedContentLevel={{@controller.contentLevel}}
        @contentLevelPositions={{this.contentLevelPositions}}
        @disabled={{this.isMissingStandards}}
      />

      {{#if this.session.currentSchool.standardsSet}}
        <StandardsSetUpdatedAlert @report={{this.latestOverviewReport}} />

        {{#if (and this.latestOverviewReportData this.latestResultsReportData)}}
          {{#if this.latestOverviewReportData}}
            <StandardsOverview
              @reportData={{this.latestOverviewReportData}}
              @isReloading={{this.fetchOverviewReport.isPending}}
              @product={{Product.FP}}
            />
            <PerformanceOverview
              @reportData={{this.latestOverviewReportData}}
              @showTitle={{true}}
              @isSubco={{this.isSubco}}
              @isReloading={{this.fetchOverviewReport.isPending}}
              @isFastPhonics={{true}}
            />
          {{/if}}
          {{#if this.latestResultsReportData}}
            <StandardsResults
              @reportData={{this.latestResultsReportData}}
              @exportCsv={{this.exportResultsCsv}}
              @isReloading={{this.fetchResultsReport.isPending}}
              @product={{Product.FP}}
              @studentReportRoute="reporting.ui-scope.fast-phonics.lesson-performance"
            />
          {{/if}}

        {{else}}
          <NoReportValue @reportStates={{array this.fetchResultsReport this.fetchOverviewReport}} />
        {{/if}}
      {{else}}
        <NoStandardsSetAlert
          @hasDisco={{Boolean this.session.currentSchool.district.districtCoordinators.length}}
          @isSubco={{this.isSubco}}
          @isOneRosterEnabled={{this.isOneRosterEnabled}}
        />
      {{/if}}
    </div>
  </template>
}

export default RouteTemplate(FpStandardsPerformancePage)
